import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { Lock, ArrowRight, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";

export function useSubscriptionAccess() {
  const { user } = useAuth();
  const sub = user?.subscription || {};
  const status = sub.status || "none";
  const planId = (sub.plan_id || sub.plan || "").toLowerCase();
  const isAdmin = user?.role === "admin";
  const access = user?.access || {};

  const isTrialing = status === "trialing";
  const isActive = status === "active";
  const isFree = status === "free";
  const isExpired = status === "expired" || status === "trial_expired";

  // Plan tier (per #user req Iter3): trial=all, core=bio_age+longevity, pro=clinical phenotypes
  const isTrial = isTrialing;
  const isCore = isActive && (planId === "core" || planId === "pro" || planId === "clinic");
  const isPro = isActive && (planId === "pro" || planId === "clinic");
  const isClinic = isActive && planId === "clinic";

  const canAI = isAdmin || access.can_ai || isTrial || isActive;
  const canMealIntelligence = isAdmin || access.can_meal_intelligence || isTrial || isActive;
  // iter308 — Scope trial access to patient-safe educational tools only.
  // Trial gets: biological age, longevity, hematology, clinical phenotypes
  //             (patient-safe educational framing).
  // Trial does NOT get:  (breast-cancer risk score — MDR Rule 11
  //             borderline), epigenetic map (deep clinical tool). Those
  //             require Pro plan OR clinician role.
  const canBiologicalAge = isAdmin || isTrial || isCore;
  const canLongevity = isAdmin || isTrial || isCore;
  const canHematology = isAdmin || isTrial || isCore;
  // iter310 — Clinical Phenotypes (PAM50 breast/lung/prostate staging,
  // MS/IBD/T2DM/MAFLD/CKD/RA subtype classifiers) are MDR Class IIa
  // diagnostic decisions. Hard-gate to Admin or verified Clinician role
  // regardless of subscription tier. Patients see a clinician-only notice.
  const isClinician = user?.role === "clinician";
  const canClinicalPhenotypes = isAdmin || isClinician;
  const canPhenotypeSynergy = isAdmin || isClinician;
  // iter380 — Phenotype Pathway Picks + Mind-Body Interventions also
  // surface KEGG-pathway and behavioural-medicine recommendations that
  // require clinical interpretation. Patient view shows a clinician-only
  // notice (same MDR Rule 11 rationale).
  const canPhenotypePathways = isAdmin || isClinician;
  const canMindBody = isAdmin || isClinician;
  const canTyrerCuzick = isAdmin || isPro; // was: trial also
  const canEpigeneticMap = isAdmin || isPro; // was: trial also
  // iter309 — Clinical Analysis + Nephrology calculators require paid tier
  // (or trial). Free/expired users see UpgradeLock. Output is patient-safe
  // framed (iter305) so regulatory risk is low, but the feature consumes
  // AI credits and delivers premium value → gate free users.
  const canClinicalAnalysis = isAdmin || isTrial || isCore;
  const canNephrologyCalculators = isAdmin || isTrial || isCore;
  // Deep clinical tools — nephro calculators, dialysis prescription, SOAP
  // notes — restricted to Clinician role regardless of subscription tier.
  const canDeepClinicalTools = isAdmin || isClinician;

  const tier = isAdmin ? "admin"
    : isClinic ? "clinic"
    : isPro ? "pro"
    : isCore ? "core"
    : isTrial ? "trialing"
    : isFree ? "free"
    : "expired";

  return {
    canAI, canMealIntelligence,
    canBiologicalAge, canLongevity, canHematology,
    canClinicalPhenotypes, canPhenotypeSynergy, canTyrerCuzick, canEpigeneticMap,
    canPhenotypePathways, canMindBody,
    canClinicalAnalysis, canNephrologyCalculators,
    canDeepClinicalTools, isClinician,
    isFree, isExpired, isTrial, isCore, isPro, isClinic,
    tier, status, planId,
  };
}

export default function UpgradeLock({ feature }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const featureLabels = {
    ai: bg ? "AI Чат и анализи" : "AI Chat & Analysis",
    meal_intelligence: bg ? "Умно хранене" : "Meal Intelligence",
    biological_age: bg ? "Биологична възраст" : "Biological Age",
    longevity: bg ? "Longevity Score" : "Longevity Score",
    hematology: bg ? "HemoBot — Кръвна картина" : "HemoBot — Blood Panel",
    clinical_phenotypes: bg ? "Анализи" : "Analyses",
    clinical_analysis: bg ? "Клиничен анализ" : "Clinical Analysis",
    nephrology_calculators: bg ? "Нефрологични калкулатори" : "Nephrology Calculators",
    phenotype_synergy: bg ? "Phenotype Synergy Detector" : "Phenotype Synergy Detector",
    phenotype_pathways: bg ? "Препоръки по фенотипни пътища" : "Phenotype Pathway Picks",
    mind_body: bg ? "Mind-Body Interventions" : "Mind-Body Interventions",
    tyrer_cuzick: bg ? " (риск за рак на гърдата)" : " (breast cancer risk)",
    epigenetic_map: bg ? "Епигенетична карта" : "Epigenetic Map",
  };
  const planRequired = {
    biological_age: "core", longevity: "core", hematology: "core",
    clinical_analysis: "core", nephrology_calculators: "core",
    // iter310 — Clinical Phenotypes + Phenotype Synergy contain MDR Class IIa
    // diagnostic classifiers (cancer subtyping, MS/IBD/T2DM staging) —
    // restricted to verified Clinician role, NOT a subscription upgrade.
    clinical_phenotypes: "clinician_only", phenotype_synergy: "clinician_only",
    // iter380 — Phenotype Pathway Picks + Mind-Body Interventions surface
    // pathway-level KEGG mappings + behavioural-medicine recommendations
    // which require clinical interpretation per MDR Rule 11.
    phenotype_pathways: "clinician_only", mind_body: "clinician_only",
    tyrer_cuzick: "pro",
    epigenetic_map: "pro",
  };
  const requiredPlan = planRequired[feature];
  const isClinicianOnly = requiredPlan === "clinician_only";

  // Iconography + CTA differ for clinician-only features — we don't want to
  // mislead patients into thinking they can "upgrade" to diagnostic tools.
  const IconComp = isClinicianOnly ? Stethoscope : Lock;

  return (
    <div className={`relative rounded-xl overflow-hidden ${isDark ? "bg-[hsl(223,47%,8%)] border border-[hsl(223,47%,15%)]" : "bg-white border border-gray-100 shadow-sm"}`} data-testid={`upgrade-lock-${feature}`}>
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isDark ? (isClinicianOnly ? "bg-sky-500/10" : "bg-amber-500/10") : (isClinicianOnly ? "bg-sky-50" : "bg-amber-50")}`}>
          <IconComp className={`w-7 h-7 ${isClinicianOnly ? "text-sky-500" : "text-amber-500"}`} />
        </div>
        <h3 className={`text-base font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {featureLabels[feature] || feature}
        </h3>
        <p className={`text-sm mb-5 max-w-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {isClinicianOnly
            ? (bg
              ? "Този модул съдържа клинични класификатори (PAM50, стадиране на рак, IBD/MS/T2DM подтипове), предназначени само за сертифицирани здравни специалисти. Според EU MDR Rule 11 диагностични решения не се предоставят директно на пациенти. Обсъдете резултатите си с Вашия лекар."
              : "This module contains clinical classifiers (PAM50, cancer staging, IBD/MS/T2DM subtypes) intended for certified healthcare professionals only. Under EU MDR Rule 11 diagnostic decisions are not provided directly to patients. Please discuss your results with your doctor.")
            : requiredPlan === "core"
            ? (bg ? "Тази функция е достъпна с план Core или по-висок (или Trial)."
                  : "Available on Core plan or higher (or Trial).")
            : requiredPlan === "pro"
            ? (bg ? "Тази функция е достъпна с план Pro или по-висок (или Trial)."
                  : "Available on Pro plan or higher (or Trial).")
            : (bg
              ? "Тази функция е достъпна само за Premium абонати. Надградете плана си за пълен достъп."
              : "This feature is available for Premium subscribers only. Upgrade your plan for full access.")}
        </p>
        {isClinicianOnly ? (
          <div className="flex flex-col items-center gap-3 w-full max-w-sm">
            <Button
              onClick={() => navigate("/clinician/register")}
              className="bg-gradient-to-r from-[#0EA5E9] to-[#14B8A6] text-white px-6 h-10 rounded-xl text-sm font-semibold w-full"
              data-testid={`clinician-signup-btn-${feature}`}
            >
              {bg ? "Аз съм специалист — регистрирай ме" : "I am a clinician — sign me up"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {/* iter311 — Removed per user request iter894: patient-side
                "Искам професионален преглед от лекар (до 48ч)" CTA
                (no active clinician review pipeline yet). */}
          </div>
        ) : (
          <Button
            onClick={() => navigate("/upgrade")}
            className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white px-6 h-10 rounded-xl text-sm font-semibold"
            data-testid={`upgrade-btn-${feature}`}
          >
            {bg ? "Надградете" : "Upgrade"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
